import React, { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { BookOpen, ArrowRight, Sparkles, Landmark } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/* Tafsir Explorer index — pick a surah, read it like a structured tafsir book. */
export default function Tadabur() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const [surahs, setSurahs] = useState(null);
  const [progress, setProgress] = useState({}); // {surahNumber: completedCount}

  const legacySurah = params.get("surah");

  useEffect(() => {
    if (legacySurah) return;
    api.get("/tafsir/surahs").then(async (r) => {
      setSurahs(r.data);
      const entries = await Promise.all(
        r.data.map((s) =>
          api.get(`/tafsir/progress/${s.surah_number}`)
            .then((p) => [s.surah_number, p.data.filter((x) => x.completed).length])
            .catch(() => [s.surah_number, 0]),
        ),
      );
      setProgress(Object.fromEntries(entries));
    }).catch(() => setSurahs([]));
  }, [legacySurah]);

  // Legacy links: /app/tadabbur?surah=2&ayah=5 → open the reader at that ayah's section
  if (legacySurah) {
    const ayah = params.get("ayah");
    return <Navigate to={`/app/tadabbur/${legacySurah}${ayah ? `?ayah=${ayah}` : ""}`} replace />;
  }

  return (
    <div className="space-y-8 max-w-5xl mx-auto" data-testid="tafsir-explorer-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{t("tadabur.tadabburTafsirExplorer")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{t("tadabur.readTheQurAnLike")}</h1>
        <p className="mt-2 text-foreground/70 max-w-2xl">
          {t("tadabur.eachSurahIsOrganizedInto")}
        </p>
      </header>

      {!surahs ? (
        <div className="grid gap-5">
          {[0, 1].map((i) => <div key={i} className="hifz-card h-48 animate-pulse-soft bg-sand-200/50" />)}
        </div>
      ) : (
        <div className="grid gap-5">
          {surahs.map((s) => {
            const done = progress[s.surah_number] || 0;
            // C8 — this total genuinely includes the introduction (tracked
            // as its own completable item, `section: "intro"`, alongside
            // the N thematic sections — see /tafsir/progress). That's
            // correct data, not a bug: the bug was the *label* claiming
            // "sections" here when the nearby badge's "N thematic sections"
            // deliberately excludes the intro, making the two numbers read
            // as contradictory. Fixed by wording this one as "parts" — see
            // the JSX below — rather than changing what's counted.
            const total = s.section_count + 1; // + intro
            const pct = Math.min(100, Math.round((done / total) * 100));
            return (
              <Link
                key={s.surah_number}
                to={`/app/tadabbur/${s.surah_number}`}
                className="hifz-card hifz-card-hover group"
                data-testid={`tafsir-surah-card-${s.surah_number}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground">{t("tadabur.surahAyahs", { surah_number: s.surah_number, total_ayahs: s.total_ayahs })}</div>
                    <div className="mt-1 font-display font-semibold text-2xl text-forest-700">{s.name_en}</div>
                    <div className="text-sm text-muted-foreground">"{s.meaning}"</div>
                  </div>
                  <div className="ayah-arabic text-3xl text-foreground/85">{s.name_ar}</div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-xs">
                  <span className={`status-pill text-[10px] ${s.revelation_type === "makki" ? "status-learning" : "status-memorized"}`}>
                    <Landmark className="h-3 w-3" /> {s.revelation_type === "makki" ? t("tadabur.makki") : t("tadabur.madani")}
                  </span>
                  <span className="text-muted-foreground">{t("tadabur.thematicSections", { section_count: s.section_count })}</span>
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span title={t("tadabur.partsHint", { count: s.section_count })}>{t("tadabur.ofPartsRead", { done, total })}</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-sand-200 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-forest-600 to-gold-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="mt-4 text-xs text-forest-700 inline-flex items-center gap-1 font-medium">
                  {done > 0 ? t("tadabur.continueReading") : t("tadabur.startWithTheIntroduction")}
                  <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <div className="hifz-card bg-sand-200/50 border-dashed flex items-start gap-4" data-testid="tafsir-coming-soon">
        <div className="h-10 w-10 rounded-xl bg-white grid place-items-center text-gold-600 shrink-0">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <div className="font-display font-semibold text-forest-700">{t("tadabur.all114SurahsAreComing")}</div>
          <p className="text-sm text-foreground/70 mt-1">
            {t("tadabur.theExplorerStartsWithAl")}
          </p>
        </div>
      </div>

      <div className="text-xs text-muted-foreground flex items-center gap-2">
        <BookOpen className="h-3.5 w-3.5" />
        {t("tadabur.organizationInspiredByScholarlyTafsir")}
      </div>
    </div>
  );
}
